import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Company } from '@models/companies/company';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { RelationshipTypeLabels } from '@models/relationships/relationship-type.enum';
import { SelectOption } from '@models/ui-models/select-options';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { CompanyChangePartyNameComponent } from '../../asic-forms/company';
import { Relationship } from "@models/relationships/relationship";
import { ACNPipe } from "../../../shared/pipes/acnPipe";
import { Guid } from "../../../shared/utils/guid.helper";

export class CompanyChangePartyName extends EntityChangeData {
  static override readonly $type = 'CompanyChangePartyName';

  firstName: string;
  middleName1: string;
  middleName2: string;
  lastName: string;
  companyName: string;
  relationshipIds: string[] = [];

  constructor(data: Partial<CompanyChangePartyName> = {}) {
    super(data);

    this.firstName = data?.firstName ?? '';
    this.middleName1 = data?.middleName1 ?? '';
    this.middleName2 = data?.middleName2 ?? '';
    this.lastName = data?.lastName ?? '';
    this.companyName = data?.companyName ?? '';
    this.relationshipIds = data?.relationshipIds?.filter(Boolean) ?? [];
  }

  override toDictionary(): { key: string; value: string; }[] {
    const dict = new ChangeDictionaryHelper();

    dict.addDate('Date of Change', this.changeDate);

    // if (this.relationshipIds.length)
    //   dict.addIds('Date of Change', this.relationshipIds);


    if (this.companyName) {
      dict.add('New Company Name', this.companyName);
    } else {
      const newName = [this.firstName, this.middleName1, this.middleName2, this.lastName]
        .filter(Boolean)
        .join(' ');
      dict.add('New Name', newName);
    }

    return dict.dictionary;
  }

  override fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    modalRef = this.fillModalWithCompany(modalRef, actualCompany, true);
    (modalRef.componentInstance as CompanyChangePartyNameComponent).companyChangeData.documentId = document.documentId;
    return modalRef;
  }

  override fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit: boolean): NgbModalRef {
    const instance = modalRef.componentInstance as CompanyChangePartyNameComponent;

    instance.isEdit = isEdit;
    instance.formModel = structuredClone(this);
    instance.companyChangeData = new CompanyChangeData(company);

    const relationshipsOptions: SelectOption<string>[] = [];
    const entityRelationships: Record<string, Relationship[]> = {};

    company.relationships
      .filter((relationship) => !relationship.end && Guid.isValidGuid(relationship.relationshipId))
      .filter((relationship) => !relationship.entityDataOverride) // delete line when companies available for change name
      .forEach((relationship) => {
        if (relationship.individualDataOverride && relationship.individualId) {
          // Individual Relationship

          if (!entityRelationships[relationship.individualId]) {
            // New individual relationships

            entityRelationships[relationship.individualId] = [relationship];
          } else {
            entityRelationships[relationship.individualId].push(relationship);
          }

        } else if (relationship.entityDataOverride) {
          // Corporate Relationship

          if (!entityRelationships[relationship.relationshipId]) {
            entityRelationships[relationship.relationshipId] = [relationship];
          } else {
            entityRelationships[relationship.relationshipId].push(relationship);
          }
        }
      });

    Object.entries(entityRelationships)
      .forEach(([entityId, relationships]) => {
        const types = relationships.map((relationship) => RelationshipTypeLabels[relationship.type]).join(', ');

        if (relationships[0].individualDataOverride) {
          // Individual Relationship option
          relationshipsOptions.push({
            label: `${ relationships[0].individualDataOverride?.fullNameLabel } (${ types })`,
            value: entityId
          });
        } else if (relationships[0].entityDataOverride) {
          // Corporate Relationship option
          const label = relationships[0].entityDataOverride?.entityNumber
            ? `${ relationships[0].entityDataOverride?.name } - ACN ${ ACNPipe.formatValue(relationships[0].entityDataOverride?.entityNumber) } (${ types })`
            : `${ relationships[0].entityDataOverride?.name } (${ types })`;

          relationshipsOptions.push({
            label,
            value: entityId
          });
        }
      });

    instance.officersChangeNameOptions = relationshipsOptions;
    instance.entityRelationships = entityRelationships;

    return modalRef;
  }
}
